import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { Text } from '../../components/text'

import { Color } from '../../theme/color'

interface TabItems<T> {
  text: string
  value: T
}

interface TabProps<T> {
  items: TabItems<T>[]
  isScrolling?: boolean
  activeIndex?: number
  onChange?: (value: T) => void
}

export function Tab<T>({ items, isScrolling, activeIndex: _activeIndex, onChange }: TabProps<T>) {
  const [activeIndex, setActiveIndex] = useState<number>(_activeIndex ?? 0)

  useEffect(() => {
    if (typeof _activeIndex === 'undefined') return
    setActiveIndex(_activeIndex)
  }, [_activeIndex])

  return (
    <TabContainer isScrolling={isScrolling}>
      {items.map(({ text, value }, i) => {
        const isActive = activeIndex === i

        const handleActive = () => {
          typeof _activeIndex === 'undefined' && setActiveIndex(i)
          onChange?.(value)
        }

        return (
          <TabButtonContainer key={i} isActive={isActive} onClick={handleActive}>
            <Text size={16} weight={isActive ? 700 : 500} color={isActive ? 'primary_5' : 'gray_5'}>
              {text}
            </Text>
          </TabButtonContainer>
        )
      })}
    </TabContainer>
  )
}

export type { TabProps, TabItems }

const TabContainer = styled.div<{ isScrolling?: boolean }>`
  display: flex;
  flex-shrink: 0;
  ${({ isScrolling }) =>
    isScrolling
      ? `
        position: sticky;
        top: 45px;
      `
      : ''}
  width: 100%;
  height: 40px;
  padding: 0 28px;
  border-bottom: 1px solid ${Color.line};
  background-color: ${Color.white};
  > * {
    flex-grow: 1;
  }
`

const TabButtonContainer = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  ${({ isActive }) =>
    isActive
      ? `
          &::after {
            display: block;
            content: '';
            position: absolute;
            bottom: -1px;
            left: 50%;
            width: 80px;
            height: 1px;
            border-bottom: 2px solid ${Color.primary_5};
            transform: translateX(-50%);
          }
        `
      : ''}
`
